
import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Package, Truck, Check, Clock, MapPin, FileText, ExternalLink, AlertCircle, ShoppingCart, User
} from '../components/Icons';
import { Card, PageHeader, Button, Badge, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface TrackingStep {
  label: string;
  date: string;
  status: 'completed' | 'current' | 'upcoming';
  description: string;
}

const OrderTracking: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Mock data for the specific order
  const orderDetails = useMemo(() => ({
    id: orderId || 'PO-2025-082',
    supplier: 'Supplier Portal',
    customer: user?.companyName || 'VMI Enterprise AG',
    orderDate: 'Oct 24, 2025',
    estDelivery: 'Oct 28, 2025',
    status: 'IN TRANSIT',
    method: 'Express Freight',
    items: [
      { id: 'ITM-001-A', name: 'Industrial Ball Bearing X200', qty: 150, price: 25.00 },
      { id: 'ITM-009-I', name: 'Conveyor Belt segment', qty: 4, price: 475.00 }
    ],
    trackingNum: '1Z999AA10123456784'
  }), [orderId, user]);

  const timeline: TrackingStep[] = [
    { label: 'Order Requested', date: 'Oct 24, 09:15', status: 'completed', description: 'Replenishment trigger confirmed by Supplier' },
    { label: 'Processing', date: 'Oct 24, 14:30', status: 'completed', description: 'Picking and packing at Central Hub' },
    { label: 'In Transit', date: 'Oct 25, 08:00', status: 'current', description: 'Departed sorting facility in Zurich' },
    { label: 'Delivery', date: 'Est. Oct 28', status: 'upcoming', description: 'Expected arrival at Customer Dock 4' }
  ];

  const totalAmount = orderDetails.items.reduce((acc, item) => acc + (item.qty * item.price), 0);

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={`Track Order ${orderDetails.id}`}
        breadcrumbs={[{ label: 'Orders' }, { label: 'Tracking' }]}
        onBack={() => navigate(-1)}
        actions={
          <div className="flex gap-3">
             <Button variant="outline" size="sm">
                <FileText size={14} className="mr-2" /> VIEW INVOICE
             </Button>
             <Button size="sm">
                <ExternalLink size={14} className="mr-2" /> CARRIER SITE
             </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        {/* Left Column: Timeline & Details */}
        <div className="xl:col-span-8 space-y-8">
           {/* Summary Strip */}
           <Card className="p-0 overflow-hidden border-none shadow-sm">
              <div className="bg-brand-dark text-white p-6 flex flex-wrap items-center justify-between gap-6">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                       <Truck size={24} className="text-brand-bright" />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-white/50">Current Status</p>
                       <h3 className="text-xl font-bold tracking-tight uppercase">In Transit - Arriving Tuesday</h3>
                    </div>
                 </div>
                 <div className="flex items-center gap-8">
                    <div className="text-right">
                       <p className="text-[10px] font-black uppercase tracking-widest text-white/50">Tracking #</p>
                       <p className="text-sm font-mono font-bold">{orderDetails.trackingNum}</p>
                    </div>
                    <div className="h-10 w-px bg-white/10"></div>
                    <div className="text-right">
                       <p className="text-[10px] font-black uppercase tracking-widest text-white/50">Estimated Arrival</p>
                       <p className="text-sm font-bold">{orderDetails.estDelivery}</p>
                    </div>
                 </div>
              </div>
           </Card>

           {/* Vertical Timeline */}
           <Card className="p-10">
              <h3 className="text-sm font-black uppercase tracking-widest text-brand-dark dark:text-white mb-10">Shipping Milestone</h3>
              <div className="space-y-0">
                 {timeline.map((step, idx) => (
                    <div key={idx} className="flex gap-8 relative pb-10 last:pb-0">
                       {idx !== timeline.length - 1 && (
                          <div className={`absolute left-[11px] top-7 bottom-0 w-0.5 ${step.status === 'completed' ? 'bg-brand-medium' : 'bg-gray-100 dark:bg-gray-800'}`}></div>
                       )}
                       
                       <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 border-2 transition-all ${
                          step.status === 'completed' 
                            ? 'bg-brand-medium border-brand-medium text-white' 
                            : step.status === 'current'
                              ? 'bg-white border-brand-medium text-brand-medium animate-pulse'
                              : 'bg-white border-gray-200 text-gray-300'
                       }`}>
                          {step.status === 'completed' ? <Check size={12} strokeWidth={4} /> : <div className="w-1.5 h-1.5 rounded-full bg-current"></div>}
                       </div>

                       <div className="flex flex-col sm:flex-row sm:items-start justify-between flex-1 gap-2">
                          <div className="space-y-1">
                             <p className={`text-sm font-black uppercase tracking-wider ${step.status === 'upcoming' ? 'text-gray-400' : 'text-brand-dark dark:text-white'}`}>
                                {step.label}
                             </p>
                             <p className="text-xs text-gray-500 font-medium max-w-md">{step.description}</p>
                          </div>
                          <div className="text-right">
                             <p className="text-[11px] font-black text-brand-medium uppercase tracking-widest">{step.date}</p>
                          </div>
                       </div>
                    </div>
                 ))}
              </div>
           </Card>

           {/* Item Breakdown */}
           <Card className="overflow-hidden border-t-4 border-t-brand-medium">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-[#1A1A1A]">
                 <h3 className="text-xs font-black uppercase tracking-widest text-gray-500">Items in this shipment</h3>
              </div>
              <Table>
                 <Thead>
                    <Tr isHeader>
                       <Th>Product</Th>
                       <Th className="text-right">Quantity</Th>
                       <Th className="text-right">Unit Price</Th>
                       <Th className="text-right">Total</Th>
                    </Tr>
                 </Thead>
                 <Tbody>
                    {orderDetails.items.map((item) => (
                       <Tr key={item.id}>
                          <Td>
                             <div className="font-bold text-brand-dark dark:text-white">{item.name}</div>
                             <div className="text-[10px] font-mono text-gray-400 uppercase">{item.id}</div>
                          </Td>
                          <Td className="text-right font-black">{item.qty}</Td>
                          <Td className="text-right text-gray-500">CHF {item.price.toFixed(2)}</Td>
                          <Td className="text-right font-bold text-brand-dark dark:text-white">CHF {(item.qty * item.price).toFixed(2)}</Td>
                       </Tr>
                    ))}
                 </Tbody>
              </Table>
           </Card>
        </div>

        {/* Right Column: Logistics Summary */}
        <div className="xl:col-span-4 space-y-8">
           <Card className="p-8 space-y-6">
              <h3 className="text-xs font-black uppercase tracking-widest text-brand-dark dark:text-white border-b border-gray-100 dark:border-gray-800 pb-4">Fulfillment Details</h3>
              
              <div className="space-y-6">
                 <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-light rounded-sm flex items-center justify-center text-brand-dark shrink-0">
                       <User size={18} />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Supplier</p>
                       <p className="text-sm font-bold text-brand-dark dark:text-white">{orderDetails.supplier}</p>
                    </div>
                 </div>

                 <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-light rounded-sm flex items-center justify-center text-brand-dark shrink-0">
                       <MapPin size={18} />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Destination Hub</p>
                       <p className="text-sm font-bold text-brand-dark dark:text-white">{orderDetails.customer}</p>
                       <p className="text-xs text-gray-500">Main Warehouse, Dock 4</p>
                    </div>
                 </div>

                 <div className="flex gap-4">
                    <div className="w-10 h-10 bg-brand-light rounded-sm flex items-center justify-center text-brand-dark shrink-0">
                       <ShoppingCart size={18} />
                    </div>
                    <div>
                       <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Order Value</p>
                       <p className="text-sm font-bold text-brand-dark dark:text-white">CHF {totalAmount.toLocaleString('de-CH')}</p>
                    </div>
                 </div>
              </div>
           </Card>

           <Card className="p-8 bg-brand-light/30 border-dashed border-2 border-brand-medium/20 text-center space-y-4">
              <AlertCircle size={32} className="text-brand-medium mx-auto opacity-50" />
              <div className="space-y-1">
                 <h4 className="text-sm font-bold text-brand-dark dark:text-white uppercase tracking-widest">Support Inquiries</h4>
                 <p className="text-xs text-gray-500 font-medium leading-relaxed">
                    Experiencing delays or need to modify this shipment? Contact your dedicated supply chain manager.
                 </p>
              </div>
              <Button variant="outline" className="w-full h-11 text-[10px] font-black uppercase tracking-widest">
                 OPEN LOGISTICS TICKET
              </Button>
           </Card>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
